<script setup lang="ts">
export interface WidgetSearchInputProps {
    loading?: boolean
    label: string
    placeholder: string
}

export interface WidgetSearchInputEmits {
    (e: 'submitSearch', value: string | undefined): void
    (e: 'resetSearch'): void
    (e: 'onFocus' | 'onBlur', event: FocusEvent): void
}

withDefaults(defineProps<WidgetSearchInputProps>(), {
    loading: false,
})
const emit = defineEmits<WidgetSearchInputEmits>()

const searchTerm = defineModel<string>('searchTerm')
const searchInput = shallowRef()
const searchId = useId()
const showResetButton = computed(() => !!searchTerm.value)

function onFocus (e: FocusEvent): void { emit('onFocus', e) }
function onBlur (e: FocusEvent): void { emit('onBlur', e) }

function submitSearch (): void {
    emit('submitSearch', searchTerm.value)
}
function resetSearch () {
    searchTerm.value = ''
    searchInput.value.componentInput.foundationInput.focus()
    emit('resetSearch')
}
</script>

<template>
    <ComponentTextInput
        :id="`search-${searchId}`"
        ref="searchInput"
        v-model="searchTerm"
        :label="label"
        :placeholder="placeholder"
        :show-label="false"
        :description="$t('search.input.description')"
        size="sm"
        bordered
        input-css="header-search-input pr-9"
        class="w-full items-center"
        @on-focus="onFocus"
        @on-blur="onBlur"
    >
        <template #iconAfter>
            <FoundationButton
                v-if="!showResetButton"
                type="button"
                variant="ghost"
                size="small"
                square
                :disabled="loading"
                @click="submitSearch"
            >
                <span class="sr-only">{{ $t('search.input.submit') }}</span>
                <FoundationIcon v-if="!loading" name="search" />
                <FoundationIcon v-else name="loading" class="animate-spin" />
            </FoundationButton>

            <FoundationButton
                v-if="showResetButton"
                type="button"
                variant="ghost"
                size="small"
                square
                :disabled="loading"
                @click="resetSearch()">
                <span class="sr-only">{{ $t('search.input.reset') }}</span>
                <FoundationIcon v-if="loading" name="loading" class="animate-spin" />
                <FoundationIcon v-else name="x" />
            </FoundationButton>
        </template>
    </ComponentTextInput>
</template>
